import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, AlertTriangle, Clock, Check, CheckCheck, ExternalLink, Loader2, X, Hand, Trash2, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SEV = {
  critical: { label: "Critical", icon: AlertTriangle, cls: "text-red-600", badge: "bg-red-100 text-red-700" },
  high: { label: "High", icon: Clock, cls: "text-amber-600", badge: "bg-amber-100 text-amber-800" },
  normal: { label: "Normal", icon: Bell, cls: "text-slate-500", badge: "bg-slate-100 text-slate-600" },
};

// Map a reminder type to the Deal Room tab that resolves it (approved routes only).
const TYPE_TAB = {
  missing_signature: "contracts", delivery_deadline: "logistics", unresolved_dispute: "disputes",
  shipment_exception: "logistics", expiring_provider_offer: "routing", expiring_carrier_offer: "logistics",
  escrow_approval: "escrow", release_approval: "escrow", missing_document: "vault",
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sev, setSev] = useState("all");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [dealFilter, setDealFilter] = useState("all");
  const [archived, setArchived] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const ref = useRef(null);

  const PAGE = 50;
  // Server-side severity/unread filtering + pagination so a specific-severity or lower-severity
  // item is fetched from the FULL set, never buried behind the old arbitrary 500-cap window.
  const buildParams = useCallback((skip) => {
    if (reviewed) return { reviewed: true, skip, limit: PAGE };
    if (archived) return { archived: true, skip, limit: PAGE };
    const p = { skip, limit: PAGE };
    if (sev !== "all") p.severity = sev;
    if (onlyUnread) p.unread = true;
    return p;
  }, [archived, reviewed, sev, onlyUnread]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reminders", { params: buildParams(0) });
      setItems(data.reminders || []);
      setHasMore(!!data.has_more);
      setTotal(data.total || 0);
      if (!archived && !reviewed) setUnread(data.unread || 0);
      setError(false);
    } catch (err) {
      setError(true);
      if (open) toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, [open, archived, reviewed, buildParams]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/reminders", { params: buildParams(items.length) });
      setItems((prev) => [...prev, ...(data.reminders || [])]);
      setHasMore(!!data.has_more);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load more");
    } finally { setLoadingMore(false); }
  }, [buildParams, items.length]);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (r) => {
    if (r.read) return;
    try { await api.post(`/reminders/${r.reminder_id}/read`); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const markAllRead = async () => {
    try { await api.post("/reminders/read-all"); toast.success("All notifications marked read"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const ack = async (r) => {
    try { await api.post(`/reminders/${r.reminder_id}/acknowledge`); toast.success("Acknowledged"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const markReviewed = async (r) => {
    try { await api.post(`/reminders/${r.reminder_id}/mark-reviewed`); toast.success("Marked reviewed"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openWorkflow = async (r) => {
    await markRead(r);
    setOpen(false);
    // Absolute-path routes (e.g. the weekly workload digest) navigate directly.
    if (r.route && r.route.startsWith("/")) { navigate(r.route); return; }
    // Prefer the backend-provided route; fall back to the type map, then Coach.
    const tab = r.route || TYPE_TAB[r.reminder_type] || "coach";
    const ref = r.ref ? `&ref=${encodeURIComponent(r.ref)}` : "";
    navigate(`/deals?deal=${r.deal_id}&tab=${tab}${ref}`);
  };

  // One-tap Claim from an eligible unassigned-contract notification. Reuses the SAME race-safe
  // claim endpoint as the Reviewer Workload page — no second assignment path. Fails cleanly if
  // the contract was already claimed by someone else.
  const claimFromBell = async (r) => {
    try {
      await api.post(`/contracts/${r.ref}/claim`);
      toast.success("Contract claimed — it's now assigned to you");
      setOpen(false);
      await load();
      navigate(`/deals?deal=${r.deal_id}&tab=contracts&ref=${encodeURIComponent(r.ref)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not claim — it may already be taken");
      await load();
    }
  };

  const cleanup = async () => {
    try {
      const { data } = await api.post("/reminders/cleanup");
      toast.success(data.dismissed ? `Cleared ${data.dismissed} resolved/old` : "Nothing to clear");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Cleanup failed");
    }
  };

  const restore = async (r) => {
    try {
      await api.post(`/reminders/${r.reminder_id}/restore`);
      toast.success("Restored to your bell");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not restore");
    }
  };

  // Weekly-digest metric → Reviewer Workload page with that exact filter pre-applied.
  const digestGo = async (r, params) => {
    await markRead(r);
    setOpen(false);
    const qs = new URLSearchParams(params).toString();
    navigate(qs ? `/reviewer-workload?${qs}` : "/reviewer-workload");
  };

  const deals = Array.from(new Set(items.map((r) => r.deal_id))).filter(Boolean);
  // On Reviewed/Archived tabs the server already returns the full scoped set; the
  // severity/unread controls don't apply there, so skip the client-side sev/unread
  // filter (else picking a specific severity would wrongly hide reviewed/archived items).
  const shown = items.filter((r) =>
    (reviewed || archived || sev === "all" || r.severity === sev) &&
    (reviewed || archived || !onlyUnread || !r.read) &&
    (dealFilter === "all" || r.deal_id === dealFilter));

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen((o) => !o)}
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100"
        aria-label="Notifications">
        <Bell size={20} />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="notif-new absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown"
          className="absolute right-0 z-50 mt-2 w-[min(92vw,384px)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h3 className="font-heading text-sm font-bold text-[#0A0F1C]">Notifications</h3>
            <div className="flex items-center gap-2">
              {!archived && (
                <>
                  <button data-testid="notification-mark-all-read" onClick={markAllRead}
                    className="flex items-center gap-1 text-[11px] font-medium text-[#2563EB] hover:underline">
                    <CheckCheck size={12} /> Mark all read
                  </button>
                  <button data-testid="notification-cleanup" onClick={cleanup}
                    className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:underline">
                    <Trash2 size={12} /> Clear resolved/old
                  </button>
                </>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2">
            <div className="flex rounded-md border border-slate-300 overflow-hidden text-[11px]">
              <button data-testid="notification-tab-active" onClick={() => { setArchived(false); setReviewed(false); }}
                className={`px-2 py-1 font-medium ${!archived && !reviewed ? "bg-[#2563EB] text-white" : "text-slate-600"}`}>Active</button>
              <button data-testid="notification-tab-reviewed" onClick={() => { setReviewed(true); setArchived(false); }}
                className={`px-2 py-1 font-medium ${reviewed ? "bg-[#2563EB] text-white" : "text-slate-600"}`}>Reviewed</button>
              <button data-testid="notification-tab-archived" onClick={() => { setArchived(true); setReviewed(false); }}
                className={`px-2 py-1 font-medium ${archived ? "bg-[#2563EB] text-white" : "text-slate-600"}`}>Archived</button>
            </div>
            <select data-testid="notification-filter-severity" value={sev} onChange={(e) => setSev(e.target.value)}
              className="h-7 rounded-md border border-slate-300 px-2 text-xs">
              <option value="all">All severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
            </select>
            {deals.length > 1 && (
              <select data-testid="notification-filter-deal" value={dealFilter} onChange={(e) => setDealFilter(e.target.value)}
                className="h-7 max-w-[140px] rounded-md border border-slate-300 px-2 text-xs">
                <option value="all">All deals</option>
                {deals.map((d) => <option key={d} value={d}>{d.slice(0, 12)}</option>)}
              </select>
            )}
            <label data-testid="notification-filter-unread" className="flex items-center gap-1 text-xs text-slate-600">
              <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} /> Unread
            </label>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
            ) : error ? (
              <div data-testid="notification-error" className="px-4 py-10 text-center text-sm text-slate-500">
                Couldn't load notifications. <button onClick={load} className="text-[#2563EB] hover:underline">Retry</button>
              </div>
            ) : shown.length === 0 ? (
              <div data-testid="notification-empty" className="px-4 py-12 text-center text-sm text-slate-400">
                <Bell size={22} className="mx-auto mb-2 text-slate-300" /> You're all caught up.
              </div>
            ) : (
              <ul>
                {shown.map((r) => {
                  const s = SEV[r.severity] || SEV.normal;
                  const Icon = s.icon;
                  return (
                    <li key={r.reminder_id} data-testid={`notification-item-${r.reminder_id}`}
                      onClick={() => markRead(r)}
                      className={`cursor-pointer border-b border-slate-50 px-4 py-3 transition-colors hover:bg-slate-50 ${r.read ? "" : "bg-blue-50/40"}`}>
                      <div className="flex items-start gap-2.5">
                        <Icon size={16} className={`mt-0.5 shrink-0 ${s.cls}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`rounded px-1.5 text-[10px] font-semibold ${s.badge}`}>{s.label}</span>
                            {r.audience_role && <span className="rounded bg-slate-800 px-1.5 text-[10px] font-medium text-white">STAFF</span>}
                            {["missed", "escalated"].includes(r.escalation_stage) && (
                              <span className="rounded bg-red-600 px-1.5 text-[10px] font-semibold text-white uppercase">{r.escalation_stage}</span>
                            )}
                            {!r.read && <span className="notif-new ml-auto h-2 w-2 rounded-full bg-[#2563EB]" />}
                          </div>
                          <p className="mt-1 text-sm text-[#0A0F1C]">{r.text}</p>
                          {r.reminder_type === "reviewer_workload_digest" && r.digest && (
                            <div data-testid={`digest-chips-${r.reminder_id}`} className="mt-1.5 flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
                              {[
                                { label: `Open ${r.digest.total ?? 0}`, params: {} },
                                { label: `Awaiting ${r.digest.counts?.awaiting_initial_review ?? 0}`, params: { status: "awaiting_initial_review" } },
                                { label: `Re-review ${r.digest.counts?.re_review_required ?? 0}`, params: { status: "re_review_required" } },
                                { label: `Overdue ${r.digest.counts?.overdue_attention ?? 0}`, params: { overdue: "true" } },
                                { label: `Unassigned ${r.digest.unassigned ?? 0}`, params: { reviewer: "unassigned" } },
                                { label: `Approved ${r.digest.counts?.approved_awaiting_signature ?? 0}`, params: { status: "approved_awaiting_signature" } },
                                { label: `High risk ${r.digest.high_priority ?? 0}`, params: { priority: "high" } },
                              ].map((chip) => (
                                <button key={chip.label} data-testid={`digest-chip-${chip.label.split(" ")[0].toLowerCase()}-${r.reminder_id}`}
                                  onClick={() => digestGo(r, chip.params)}
                                  className="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:border-[#2563EB] hover:text-[#2563EB]">
                                  {chip.label}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-400">
                            {r.deal_id && <span>Deal {String(r.deal_id).slice(0, 12)}</span>}
                            {r.due_date && <span>Due {new Date(r.due_date).toLocaleDateString()}</span>}
                          </div>
                          {reviewed && (
                            <div data-testid={`notification-reviewed-trail-${r.reminder_id}`} className="mt-1.5 rounded bg-slate-50 border border-slate-200 px-2 py-1.5 text-[11px] text-slate-500">
                              <div><span className="font-semibold text-slate-600 capitalize">{String(r.reminder_type || "").replace(/_/g, " ")}</span> · <span className="text-emerald-600 font-medium">{r.resolution_state || "reviewed"}</span></div>
                              <div>Reviewed by <span className="font-medium text-slate-700">{r.reviewed_by_name || r.reviewed_by}</span>{r.reviewed_role ? ` (${String(r.reviewed_role).replace(/_/g, " ")})` : ""}</div>
                              {r.reviewed_at && <div>Reviewed {new Date(r.reviewed_at).toLocaleString()}</div>}
                              {r.created_at && <div>Original alert {new Date(r.created_at).toLocaleString()}</div>}
                            </div>
                          )}
                          {!reviewed && (
                          <div className="mt-1.5 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {archived ? (
                              <button data-testid={`notification-restore-${r.reminder_id}`} onClick={() => restore(r)}
                                className="inline-flex items-center gap-1 rounded bg-[#2563EB] px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700">
                                <RotateCcw size={10} /> Restore
                              </button>
                            ) : (
                              <>
                                {r.reminder_type === "contract_claim_available" && r.ref && (
                                  <button data-testid={`notification-claim-${r.reminder_id}`} onClick={() => claimFromBell(r)}
                                    className="inline-flex items-center gap-1 rounded bg-[#047857] px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-emerald-800">
                                    <Hand size={10} /> Claim
                                  </button>
                                )}
                                {r.reminder_type === "new_signup" ? (
                                  <button data-testid={`notification-reviewed-${r.reminder_id}`} onClick={() => markReviewed(r)}
                                    className="inline-flex items-center gap-1 rounded bg-[#2563EB] px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-blue-700">
                                    <Check size={10} /> Mark reviewed
                                  </button>
                                ) : (
                                  <button data-testid={`notification-ack-${r.reminder_id}`} onClick={() => ack(r)}
                                    className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:border-slate-400">
                                    <Check size={10} /> Acknowledge
                                  </button>
                                )}
                              </>
                            )}
                            <button data-testid={`notification-open-${r.reminder_id}`} onClick={() => openWorkflow(r)}
                              className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:border-slate-400">
                              <ExternalLink size={10} /> Open
                            </button>
                          </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {!loading && !error && hasMore && (
              <div className="px-4 py-3 text-center">
                <button data-testid="notification-load-more" onClick={loadMore} disabled={loadingMore}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-[#2563EB] hover:text-[#2563EB] disabled:opacity-50">
                  {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null}
                  Load more ({items.length} of {total})
                </button>
              </div>
            )}
          </div>
          <p className="border-t border-slate-100 px-4 py-2 text-[10px] text-slate-400">
            In-app notifications (preview). Email is preview-safe; SMS is not configured.
          </p>
        </div>
      )}
    </div>
  );
}
